import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react'
import { ConfigProvider } from 'antd'
import { useLocation } from 'react-router-dom'

import { URL_CONST } from 'utils/urlConsts'

import { useLocalStorage } from '@uidotdev/usehooks'

const THEME_STORAGE_KEY = 'ironscales-theme'

const ThemeContext = createContext({
  theme: 'light',
  effectiveTheme: 'light',
  setTheme: () => {},
})

const THEMES = {
  LIGHT: 'light',
  DARK: 'dark',
  SYSTEM: 'system',
}

const VALID_THEMES = [THEMES.LIGHT, THEMES.DARK, THEMES.SYSTEM]

// `useLocalStorage` (@uidotdev/usehooks) JSON.parses the stored value and THROWS
// during render on malformed input (e.g. a legacy raw `dark` string written
// without JSON encoding). ThemeProvider wraps the entire app, so a corrupt value
// white-screens everything. Sanitize the key once at module load: drop anything
// that isn't a valid, JSON-encoded theme and let the hook fall back to LIGHT.
;(() => {
  if (typeof window === 'undefined') return
  try {
    const raw = window.localStorage.getItem(THEME_STORAGE_KEY)
    if (raw === null) return
    if (!VALID_THEMES.includes(JSON.parse(raw))) {
      window.localStorage.removeItem(THEME_STORAGE_KEY)
    }
  } catch (e) {
    window.localStorage.removeItem(THEME_STORAGE_KEY)
  }
})()

function getSystemPrefersDark() {
  if (typeof window === 'undefined') return false
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

export function isLoginSigninPath(pathname) {
  const normalized = pathname.replace(/\/$/, '') || '/'
  return normalized === URL_CONST.login.signin
}

export function resolveDocumentTheme(pathname, effectiveTheme) {
  if (isLoginSigninPath(pathname)) {
    return THEMES.LIGHT
  }

  return effectiveTheme
}

/**
 * Applies `data-theme` on <html>. Must render inside BrowserRouter + ThemeProvider.
 * Login (`/signin`) is light-only: user preference stays in localStorage but tokens
 * resolve via light-theme.css while on that route.
 */
export function ThemeDocumentSync() {
  const { effectiveTheme } = useTheme()
  const { pathname } = useLocation()

  const documentTheme = useMemo(
    () => resolveDocumentTheme(pathname, effectiveTheme),
    [pathname, effectiveTheme]
  )

  useLayoutEffect(() => {
    document.documentElement.setAttribute('data-theme', documentTheme)
  }, [documentTheme])

  return null
}

export const ThemeProvider = ({ children }) => {
  const [theme, setStoredTheme] = useLocalStorage(THEME_STORAGE_KEY, THEMES.LIGHT)

  const [systemPrefersDark, setSystemPrefersDark] = useState(getSystemPrefersDark)

  const effectiveTheme = useMemo(
    () => (theme === THEMES.SYSTEM ? (systemPrefersDark ? THEMES.DARK : THEMES.LIGHT) : theme),
    [theme, systemPrefersDark]
  )

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleChange = (e) => setSystemPrefersDark(e.matches)
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange)
      return () => mediaQuery.removeEventListener('change', handleChange)
    }
    if (mediaQuery.addListener) {
      mediaQuery.addListener(handleChange)
      return () => mediaQuery.removeListener(handleChange)
    }
    return undefined
  }, [])

  const setTheme = useCallback(
    (newTheme) => {
      if (VALID_THEMES.includes(newTheme)) {
        setStoredTheme(newTheme)
      }
    },
    [setStoredTheme]
  )

  const value = useMemo(
    () => ({
      theme,
      effectiveTheme,
      setTheme,
    }),
    [theme, effectiveTheme, setTheme]
  )

  const antdTheme = useMemo(
    () => ({
      token: {
        /* Tabs nav divider (::before); aligns antd CSS-in-JS with app light/dark borders */
        colorBorderSecondary: 'var(--color-border)',
      },
    }),
    []
  )

  return (
    <ThemeContext.Provider value={value}>
      <ConfigProvider theme={antdTheme}>{children}</ConfigProvider>
    </ThemeContext.Provider>
  )
}

export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

export { THEMES }
export default ThemeContext
